import { CancelFromInceptionConst, CheckboxTableColum, formatNumberWithComma, GeneralInfoService, getPayeeNotPOData, HCR9BenefitInformationModal, HCR9BenefitInformationResponse, Panel, PayoutChecker, PayoutPopup, PulseOpsFormat, SelectedRowProps, sharedStyle, TableField, TaskDetail, TaskDetailApi, Title } from '@pulseops/common'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Text, TouchableOpacity, View } from 'react-native'
import { PayoutMethod } from './common/PayoutMethod'
import { FieldDescription, FieldText, SectionCol, SectionContent, SectionRow } from '@pulseops/submission/common'
import { pipe } from 'fp-ts/lib/function'
import * as O from 'fp-ts/Option'
import { ZIO } from '@mxt/zio'

type CancelRiderFromInceptionProps = {
  VERDetailData: TaskDetail.VERCancelRiderFromInception
}
export const CancelRiderFromInception = (props: CancelRiderFromInceptionProps) => {
  const { t, i18n } = useTranslation()
  const [isOpenHCR9BenefitModal, setIsOpenOpenHCR9BenefitModal] = React.useState(false)
  const [HCR9BenefitInformation, setHCR9BenefitInformation] = React.useState<HCR9BenefitInformationResponse[]>([])
  const {
    lifeAssureds,
    riderNameList,
    cashOutList,
    branchName,
    methods,
    totalPayoutAmount,
    riderRefundAmount,
    // suspensePremium,
    refundableFeeAmount,
    netAmountAvailable,
    reason,
    policyNumber
  } = props.VERDetailData
  const columns: TableField.Columns[] = [
    {
      key: '1',
      title: t('CancelRiderFromInception:RiderName'),
      name: 'productionName',
      rowRender: (selectedRow: SelectedRowProps) => {
        const productCode = selectedRow.selectItem["productCode"] as string
        const productionName = selectedRow.selectItem["productionName"] as string
        const lifeNo = selectedRow.selectItem["lifeNo"] as string
        const isHCR9Product = productCode === 'HCR9'
        return isHCR9Product ? (
          <TouchableOpacity onPress={() => onOpenHCR9BenefitModal(lifeNo)}>
            <Text style={sharedStyle.underlineLink}>{productionName}</Text>
          </TouchableOpacity>
        ) : <Text>{productionName}</Text>
      }
    },
    {
      key: '2',
      title: t('requestInfo:RiskCommencementDate'),
      name: 'riskCommDate'
    },
    {
      key: '3',
      title: t('requestInfo:RiskCessationDate'),
      name: 'riskCessDate'
    },
    {
      key: '4',
      title: t('requestInfo:SumAssured'),
      name: 'sumAssured'
    },
    {
      key: '5',
      title: t('requestInfo:InstallmentPremium'),
      name: 'installmentPremium'
    }
  ]

  const displayedBenefitColumns: CheckboxTableColum[] = [
    {
      title: t('PRUFlexiHealthBenefit:Benefit'),
      fieldName: 'benefitName',
      disabled: true,
      rowRender: (selectedRow) => {
        const benefitName = selectedRow.selectItem["benefitName"] as string
        const benefitCode = selectedRow.selectItem["benefitCode"] as string
        const customedName = i18n.language === 'en' ? benefitCode : benefitName
        return <Text>{customedName}</Text>
      }
    },
    {
      title: t('claim:SumAssured'),
      fieldName: 'sumInsurance',
      disabled: true,
      rowRender: (selectedRow) => {
        const sumInsurance = selectedRow.selectItem["sumInsurance"] as number
        return <Text>{PulseOpsFormat.thousandSepartor(sumInsurance)}</Text>
      }
    },
    {
      title: t('PRUFlexiHealthBenefit:Copay'),
      fieldName: 'coPay',
      disabled: true
    },
    {
      title: t('requestInfo:InstallmentPremium'),
      fieldName: 'installPremium',
      disabled: true,
      rowRender: (selectedRow) => {
        const installPremium = selectedRow.selectItem["installPremium"] as number
        return <Text>{PulseOpsFormat.thousandSepartor(installPremium)}</Text>
      }
    },

  ]

  const reasonName = React.useMemo(() => {
    return pipe(
      CancelFromInceptionConst.reasons.find((x) => x.value === reason),
      O.fromNullable,
      O.fold(
        () => '',
        (reasonItem) => reasonItem.label
      )
    )
  }, [reason])

  const getRiderName = (riderCode: string) => {
    const item = riderNameList.find((x) => x.code === riderCode)
    return !!item ? riderCode + ' - ' + (i18n.language === 'en' ? item.name : item.nameVi) : ''
  }

  const cashlessMethod = methods.find(
    (x) => Object.values(PayoutPopup.CashlessMethod).findIndex((y) => y == x.method) >= 0
  )?.method

  const payoutData = {
    payPremium: methods.findIndex((x) => PayoutChecker(PayoutPopup.PayoutMethods.PAYPREMIUM)(x.method)) >= 0,
    repayLoan: methods.findIndex((x) => PayoutChecker(PayoutPopup.PayoutMethods.PAYLOAN)(x.method)) >= 0,
    otherPremium: methods.findIndex((x) => PayoutChecker(PayoutPopup.PayoutMethods.OTHER)(x.method)) >= 0,
    cashless: !!cashlessMethod,
    cashlessMethod: cashlessMethod as PayoutPopup.CashlessMethod,
    totalPayoutAmount: formatNumberWithComma(totalPayoutAmount),
    payouts: methods.map((x) => ({
      payoutMethod: x.method,
      detail: x.detail,
      amount: PulseOpsFormat.thousandSepartor(x.amount),
      status: x.status,
      note: x.note || '-'
    }))
  }


  const viewPayeeData = React.useMemo(() => {
    const viewData =
      cashOutList && cashOutList.length > 0
        ? getPayeeNotPOData(cashOutList as TaskDetailApi.PaymentData.CashOutList, branchName ?? '')
        : undefined
    return viewData
  }, [cashOutList])

  const onOpenHCR9BenefitModal = (lifeNo: string) => {
    if (!!policyNumber) {
      pipe(
        GeneralInfoService.getBenefitInformationForHCR9(policyNumber),
        ZIO.fold(
          (error) => {
            setHCR9BenefitInformation([])
            setIsOpenOpenHCR9BenefitModal(true)
          },
          (response) => {
            const HCR9BenefitList = response.filter((item) => item.lifeNo === lifeNo && item.sumInsurance > 0 && item.installPremium > 0)
            setHCR9BenefitInformation(HCR9BenefitList)
            setIsOpenOpenHCR9BenefitModal(true)
          }
        ),
        ZIO.unsafeRun({})
      )
    }
  }

  const onCloseHCR9BenefitModal = () => { setIsOpenOpenHCR9BenefitModal(false) }
  return (
    <View>
      <Title title={t('CancelRiderFromInception:RIDERLIST')} />
      {lifeAssureds.length > 0 &&
        lifeAssureds.map((lifeAssuredItem, index) => (
          <Panel title={lifeAssuredItem.clientName.toUpperCase()} key={index}>
            <TableField columns={columns} dataSource={lifeAssuredItem.submissionRiders.map((x) => ({ ...x, productionName: getRiderName(x.productCode) }))} />
          </Panel>
        ))}
      <Title title={t('CancelRiderFromInception:CANCELRIDERFROMINCEPTIONINFO')} />
      <SectionContent>
        <SectionRow>
          <SectionCol>
            <FieldText text={t('CancelRiderFromInception:RiderRefundAmount')}></FieldText>
            <FieldDescription text={formatNumberWithComma(riderRefundAmount)} isCurrencyUnit></FieldDescription>
          </SectionCol>
          {/* <SectionCol>
            <FieldText text={t('CancelRiderFromInception:SuspensePremium')}></FieldText>
            <FieldDescription text={formatNumberWithComma(suspensePremium)} isCurrencyUnit></FieldDescription>
          </SectionCol> */}
          <SectionCol>
            <FieldText text={t('Payout:TempRefundFee')}></FieldText>
            <FieldDescription text={formatNumberWithComma(refundableFeeAmount)} isCurrencyUnit></FieldDescription>
          </SectionCol>
        </SectionRow>
        <SectionRow sectionStyles={sharedStyle.secondLine}>
          <SectionCol>
            <FieldText text={t('CancelRiderFromInception:NetAmountAvailable')}></FieldText>
            <FieldDescription text={formatNumberWithComma(netAmountAvailable)} fieldStyles={sharedStyle.redText} isCurrencyUnit></FieldDescription>
          </SectionCol>
          <SectionCol>
            <FieldText text={t('CFI:Reason')}></FieldText>
            <FieldDescription text={reasonName}></FieldDescription>
          </SectionCol>
        </SectionRow>
      </SectionContent>
      <SectionRow sectionStyles={sharedStyle.secondLine}>
        <SectionCol sectionStyles={sharedStyle.col_12}>
          <PayoutMethod payeeData={viewPayeeData} payoutMethodData={payoutData}></PayoutMethod>
        </SectionCol>
      </SectionRow>
      <HCR9BenefitInformationModal
        isOpen={isOpenHCR9BenefitModal}
        modalTitle={t('PRUFlexiHealthBenefit:DropRiderDetailPanel')}
        HCR9BenefitInformation={HCR9BenefitInformation}
        displayedColumns={displayedBenefitColumns}
        handleClose={onCloseHCR9BenefitModal}
      />
    </View>
  )
}